import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useIsMobile } from '@app/contexts/viewport';

interface UseLandingPageCardContentParams {
  dexfSettings: any | null;
  range: string;
  rangeName: string;
  hasPersistedScene: boolean;
  enableSaveToProfile: boolean;
}

interface UseLandingPageCardContentReturn {
  pageTitleText: React.ReactNode;
  designFromScratchCard: {
    title: string;
    body: string;
  };
  openDesignCard: {
    title: string;
    body: string;
  };
}

/**
 * Calculates all card text content based on configuration and settings.
 *
 *
 * @param params - Configuration object with all dependencies
 * @returns Object containing calculated text content
 *
 */
export const useLandingPageCardContent = ({
  dexfSettings,
  range,
  rangeName,
  hasPersistedScene,
  enableSaveToProfile
}: UseLandingPageCardContentParams): UseLandingPageCardContentReturn => {
  const isMobile = useIsMobile();
  const { formatMessage } = useIntl();

  const pageTitleText = useMemo(() => {
    if (range === 'knoxhult' || range === 'metod') {
      return formatMessage(
        { id: 'storageone.designSolutionLandingPageTitleTextKnoxhult' },
        { range: rangeName }
      );
    }

    if (isMobile) {
      return (
        <>
          {formatMessage({ id: 'storageone.designSolutionLandingPageTitleStartText' })}
          <b>{` ${rangeName} `}</b>
        </>
      );
    }

    return (
      <>
        {formatMessage({ id: 'storageone.designSolutionLandingPageTitleStartText' })}
        <br />
        <span>
          <b>{rangeName}</b>
        </span>
      </>
    );
  }, [range, rangeName, formatMessage, isMobile]);

  const designFromScratchCard = useMemo(() => {
    if (!dexfSettings) {
      return {
        title: formatMessage({ id: 'storageone.landingPageBtnDesignFromScratch' }),
        body: formatMessage({ id: 'storageone.landingPageBtnDesignFromScratchDescription' })
      };
    }

    if (enableSaveToProfile) {
      const isKitchen = range === 'knoxhult' || range === 'metod';
      return {
        title: formatMessage(
          { id: 'storageone.landingPagePlanningToolHeading' },
          { range: rangeName.toUpperCase() }
        ),
        body: formatMessage(
          {
            id: isKitchen
              ? 'storageone.landingPagePlanningToolEmptySceneKitchenBody'
              : 'storageone.landingPagePlanningToolEmptySceneBody'
          },
          { range: rangeName.toUpperCase() }
        )
      };
    }

    return {
      title: formatMessage({
        id: hasPersistedScene
          ? 'storageone.landingPageContinueDesignOrStartFromScratchTitle'
          : 'storageone.landingPageBtnDesignFromScratch'
      }),
      body: formatMessage({
        id: hasPersistedScene
          ? 'storageone.landingPageContinueDesignOrStartFromScratchText'
          : 'storageone.landingPageBtnDesignFromScratchDescription'
      })
    };
  }, [dexfSettings, enableSaveToProfile, hasPersistedScene, range, rangeName, formatMessage]);

  const openDesignCard = useMemo(() => {
    if (!dexfSettings) {
      return {
        title: formatMessage({ id: 'storageone.landingPageBtnOpenDesign' }),
        body: formatMessage({ id: 'storageone.landingPageBtnOpenDesignDescription' })
      };
    }

    if (enableSaveToProfile) {
      return {
        title: formatMessage({ id: 'storageone.landingPageOpenSavedDesignTitle' }),
        body: formatMessage({ id: 'storageone.landingPageOpenSavedDesignBody' })
      };
    }

    return {
      title: formatMessage({ id: 'storageone.landingPageBtnOpenDesign' }),
      body: formatMessage({ id: 'storageone.landingPageBtnOpenDesignDescription' })
    };
  }, [dexfSettings, enableSaveToProfile, formatMessage]);

  return {
    pageTitleText,
    designFromScratchCard,
    openDesignCard
  };
};
